import styled, { css, keyframes } from 'styled-components';

// ─── Animations ──────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

const typingDot = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40%           { transform: scale(1);   opacity: 1; }
`;

// ─── Scrollbar ───────────────────────────────────────────────────────────────

const scrollbar = css`
  &::-webkit-scrollbar {
    width: var(--dx-scrollbar-size);
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
    background-clip: content-box;
  }
`;

// ─── Panel shell ─────────────────────────────────────────────────────────────

export const PanelRoot = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: ${({ $fullscreen }) => ($fullscreen ? '0' : 'auto')};
  width: ${({ $fullscreen, $width }) => ($fullscreen ? '100vw' : `${$width}px`)};
  z-index: 300;
  display: flex;
  flex-direction: row;
  background: #ffffff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  animation: ${({ $open }) => ($open ? slideIn : slideOut)} 0.22s ease-out both;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  border-left: 1px solid #e5e7eb;
  transition:
    left 0.18s ease,
    width 0.18s ease;
`;

export const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  z-index: 10;
  background: transparent;
  border-radius: 4px 0 0 4px;
  transition: background 0.15s;

  &:hover {
    background: rgba(34, 139, 230, 0.25);
  }

  &:active {
    background: rgba(34, 139, 230, 0.4);
  }
`;

// ─── Sessions sidebar ─────────────────────────────────────────────────────────

export const SessionSidebar = styled.div`
  width: ${({ $open }) => ($open ? '185px' : '0')};
  min-width: ${({ $open }) => ($open ? '185px' : '0')};
  transition:
    width 0.2s ease,
    min-width 0.2s ease;
  overflow: hidden;
  border-right: ${({ $open }) => ($open ? '1px solid #e5e7eb' : 'none')};
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
`;

export const SidebarInner = styled.div`
  width: 185px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const SidebarHeader = styled.div`
  padding: 16px 12px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

export const SidebarTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NewChatBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 12px;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid #d0ebff;
  background: #ffffff;
  color: #228be6;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  white-space: nowrap;

  &:hover {
    background: #e7f5ff;
    border-color: #228be6;
  }
`;

export const SearchBox = styled.input`
  margin: 0 12px 8px;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  color: #374151;
  background: #ffffff;
  outline: none;
  width: calc(100% - 24px);

  &:focus {
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const SessionList = styled.div`
  flex: 1;
  overflow-y: auto;
  ${scrollbar}
`;

export const SessionItem = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: ${({ $active }) => ($active ? '#228be6' : '#374151')};
  background: ${({ $active }) => ($active ? '#e7f5ff' : 'transparent')};
  border-left: 3px solid ${({ $active }) => ($active ? '#228be6' : 'transparent')};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition:
    background 0.12s,
    color 0.12s;

  &:hover {
    background: ${({ $active }) => ($active ? '#d0ebff' : '#f1f3f5')};
    color: #228be6;
  }
`;

export const SessionDate = styled.div`
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
`;

// ─── Chat pane ────────────────────────────────────────────────────────────────

export const ChatPane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// ─── Panel header ─────────────────────────────────────────────────────────────

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 52px;
  min-height: 52px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
`;

export const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  overflow: hidden;
`;

export const AgentIconWrapper = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: linear-gradient(135deg, #228be6 0%, #1971c2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
  color: white;
  line-height: 1;
`;

export const HeaderTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1d2939;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const HeaderSubtitle = styled.span`
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
`;

export const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  transition:
    background 0.12s,
    color 0.12s;
  flex-shrink: 0;

  &:hover {
    background: #f1f3f5;
    color: #374151;
  }
`;

// ─── Messages area ────────────────────────────────────────────────────────────

export const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8f9fa;
  ${scrollbar}
`;

export const MessageRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`;

export const MessageBubble = styled.div`
  max-width: 88%;
  padding: 10px 14px;
  border-radius: ${({ $isUser }) => ($isUser ? '12px 12px 0 12px' : '12px 12px 12px 0')};
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ $isUser }) => ($isUser ? '#ffffff' : '#1d2939')};
  background: ${({ $isUser }) => ($isUser ? '#228be6' : '#ffffff')};
  border: ${({ $isUser }) => ($isUser ? 'none' : '1px solid #e5e7eb')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  word-break: break-word;

  /* Markdown content styles */
  p {
    margin: 0 0 6px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  code {
    font-size: 12px;
    background: ${({ $isUser }) => ($isUser ? 'rgba(255,255,255,0.2)' : '#f1f3f5')};
    padding: 1px 5px;
    border-radius: 3px;
  }
  pre {
    background: ${({ $isUser }) => ($isUser ? 'rgba(255,255,255,0.15)' : '#f1f3f5')};
    padding: 10px;
    border-radius: 6px;
    overflow-x: auto;
    font-size: 12px;
    margin: 6px 0;
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 18px;
  }
  li {
    margin-bottom: 2px;
  }
  strong {
    font-weight: 600;
  }
  a {
    color: ${({ $isUser }) => ($isUser ? '#a5d8ff' : '#228be6')};
    text-decoration: underline;
  }
`;

export const MessageMeta = styled.div`
  font-size: 10px;
  color: #9ca3af;
  margin-top: 4px;
  padding: 0 2px;
`;

export const StreamingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 14px;
  background: #228be6;
  margin-left: 2px;
  vertical-align: middle;
  border-radius: 1px;
  animation: ${blink} 0.8s ease-in-out infinite;
`;

// ─── Typing indicator ─────────────────────────────────────────────────────────

export const TypingRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const TypingBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px 12px 12px 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #228be6;
  display: inline-block;
  animation: ${typingDot} 1.2s ease-in-out infinite;
  animation-delay: ${({ $i }) => $i * 0.15}s;
`;

// ─── Welcome / empty state ────────────────────────────────────────────────────

export const WelcomeWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
`;

export const WelcomeIcon = styled.div`
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #228be6 0%, #1971c2 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  font-size: 28px;
  color: white;
  line-height: 1;
`;

export const WelcomeTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #1d2939;
  margin: 0;
`;

export const WelcomeSubtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

export const SuggestionChip = styled.button`
  padding: 8px 14px;
  border: 1px solid #d0ebff;
  border-radius: 20px;
  background: #ffffff;
  color: #228be6;
  font-size: 12.5px;
  cursor: pointer;
  transition:
    background 0.13s,
    border-color 0.13s;
  text-align: center;

  &:hover {
    background: #e7f5ff;
    border-color: #228be6;
  }
`;

export const SuggestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
  max-width: 320px;
  align-items: center;
`;

export const PopularPromptsSection = styled.div`
  margin-top: 16px;
  width: 100%;
  max-width: 340px;
`;

export const PopularPromptsLabel = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 8px;
  text-align: center;
`;

export const PopularPromptsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

export const PopularPromptChip = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 12.5px;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.13s,
    border-color 0.13s,
    color 0.13s;

  &:hover {
    background: #f0f7ff;
    border-color: #a5d8ff;
    color: #1971c2;
  }

  &::before {
    content: '↗';
    font-size: 11px;
    color: #9ca3af;
    flex-shrink: 0;
  }
`;

// ─── Input area ───────────────────────────────────────────────────────────────

export const InputArea = styled.div`
  padding: 12px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

export const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

export const PromptsDrawer = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 12px;
  right: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 100;
`;

export const PromptsDrawerHeader = styled.div`
  padding: 10px 14px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ca3af;
  border-bottom: 1px solid #f3f4f6;
`;

export const PromptsDrawerItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 14px;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid #f9fafb;
  font-size: 13px;
  color: #1d2939;
  cursor: pointer;
  line-height: 1.4;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f0f7ff;
    color: #1971c2;
  }
`;

export const PromptsBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: ${p => (p.$active ? '#e7f5ff' : '#f9fafb')};
  color: ${p => (p.$active ? '#228be6' : '#6b7280')};
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.13s,
    color 0.13s,
    border-color 0.13s;

  &:hover {
    background: #e7f5ff;
    color: #228be6;
    border-color: #a5d8ff;
  }
`;

export const TextareaWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: flex-end;
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 44px;
  max-height: 120px;
  padding: 10px 12px 10px 34px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13.5px;
  font-family: inherit;
  color: #1d2939;
  resize: none;
  outline: none;
  line-height: 1.5;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  background: #f8f9fa;

  &:focus {
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
    background: #ffffff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const SendBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: none;
  background: ${({ disabled }) => (disabled ? '#e5e7eb' : '#228be6')};
  color: ${({ disabled }) => (disabled ? '#9ca3af' : '#ffffff')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #1971c2;
  }
`;

export const StopBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: none;
  background: #ff6b6b;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: #fa5252;
  }
`;

// ─── Step progress (inside AI streaming bubble) ───────────────────────────────

export const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

export const StepDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $done }) => ($done ? '#51cf66' : '#228be6')};
  ${({ $done }) =>
    !$done &&
    `
    animation: stepPulse 1.4s ease-in-out infinite;
    @keyframes stepPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(0.65); }
    }
  `}
`;

export const StepText = styled.span`
  font-size: 11px;
  line-height: 1.3;
  color: ${({ $done }) => ($done ? '#adb5bd' : '#495057')};
`;

export const MicBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: none;
  background: ${({ $recording }) => ($recording ? '#fa5252' : '#f1f3f5')};
  color: ${({ $recording }) => ($recording ? '#ffffff' : '#495057')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
  flex-shrink: 0;
  position: relative;

  &:hover {
    background: ${({ $recording }) => ($recording ? '#e03131' : '#e9ecef')};
  }

  ${({ $recording }) =>
    $recording &&
    `
    &::after {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 11px;
      border: 2px solid #fa5252;
      animation: micPulse 1.2s ease-in-out infinite;
    }
    @keyframes micPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(1.15); }
    }
  `}
`;

export const InputHint = styled.div`
  font-size: 11px;
  color: #9ca3af;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// ─── Session file chips ───────────────────────────────────────────────────────

export const FileChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const SessionFileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px 3px 6px;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 12px;
  color: #495057;
  max-width: 200px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: #868e96;
    flex-shrink: 0;
    line-height: 1;

    &:hover {
      color: #fa5252;
    }
  }
`;

export const PaperclipBtn = styled.button`
  position: absolute;
  left: 8px;
  bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: #9ca3af;
  flex-shrink: 0;
  border-radius: 4px;
  transition: color 0.15s;
  z-index: 1;

  &:hover {
    color: #495057;
  }
`;

// ─── Session item delete button ───────────────────────────────────────────────

export const SessionItemRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:hover > button {
    opacity: 1;
  }
`;

export const SessionDeleteBtn = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9ca3af;
  transition:
    opacity 0.12s,
    background 0.12s,
    color 0.12s;
  flex-shrink: 0;

  &:hover {
    background: #fee2e2;
    color: #dc2626;
  }
`;

// ─── Confirmation modal ───────────────────────────────────────────────────────

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  width: 320px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ModalTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #1d2939;
  margin: 0;
`;

export const ModalBody = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const ModalCancelBtn = styled.button`
  padding: 7px 16px;
  border-radius: 7px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: #f1f3f5;
  }
`;

export const ModalDeleteBtn = styled.button`
  padding: 7px 16px;
  border-radius: 7px;
  border: none;
  background: #dc2626;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: #b91c1c;
  }
`;

// ─── Inline confirmation buttons (inside AI messages) ─────────────────────────

export const ConfirmButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

export const ConfirmYesBtn = styled.button`
  padding: 6px 18px;
  border-radius: 6px;
  border: none;
  background: #228be6;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.13s;

  &:hover {
    background: #1971c2;
  }
`;

export const ConfirmNoBtn = styled.button`
  padding: 6px 18px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.13s,
    border-color 0.13s;

  &:hover {
    background: #f1f3f5;
    border-color: #d1d5db;
  }
`;

// ─── Sidebar footer & settings menu ──────────────────────────────────────────

export const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 8px 10px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
`;

export const SettingsMenuWrapper = styled.div`
  position: relative;
`;

export const SettingsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 170px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 500;
  overflow: hidden;
`;

export const SettingsMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  background: transparent;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:hover {
    background: #f1f3f5;
    color: #228be6;
  }
`;

// ─── Add Knowledge modal ──────────────────────────────────────────────────────

export const KnowledgeModalCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 28px;
  width: 520px;
  max-width: calc(100vw - 40px);
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 20px;
  ${scrollbar}
`;

export const KnowledgeModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1d2939;
  margin: 0;
`;

export const KnowledgeModalSubtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

export const KnowledgeTitleInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  font-size: 13.5px;
  color: #1d2939;
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;

  &:focus {
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const DropZone = styled.div`
  border: 2px dashed ${({ $active }) => ($active ? '#228be6' : '#d1d5db')};
  border-radius: 10px;
  background: ${({ $active }) => ($active ? '#e7f5ff' : '#f8f9fa')};
  padding: 28px 20px;
  text-align: center;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &:hover {
    border-color: #228be6;
    background: #e7f5ff;
  }
`;

export const DropZoneIcon = styled.div`
  color: ${({ $active }) => ($active ? '#228be6' : '#9ca3af')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DropZoneText = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;

  strong {
    color: #228be6;
    cursor: pointer;
  }
`;

export const DropZoneHint = styled.p`
  font-size: 11px;
  color: #9ca3af;
  margin: 0;
`;

export const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f8f9fa;
  font-size: 12.5px;
  color: #374151;

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    border: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    flex-shrink: 0;

    &:hover {
      color: #dc2626;
    }
  }
`;

export const KnowledgeDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #9ca3af;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
`;

export const KnowledgeTextarea = styled.textarea`
  width: 100%;
  min-height: 110px;
  max-height: 200px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  font-size: 13px;
  font-family: inherit;
  color: #1d2939;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  background: #f8f9fa;

  &:focus {
    border-color: #228be6;
    box-shadow: 0 0 0 2px rgba(34, 139, 230, 0.15);
    background: #ffffff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const KnowledgeModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const KnowledgeSubmitBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: ${({ disabled }) => (disabled ? '#e5e7eb' : '#228be6')};
  color: ${({ disabled }) => (disabled ? '#9ca3af' : '#ffffff')};
  font-size: 13px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.13s;

  &:hover:not(:disabled) {
    background: #1971c2;
  }
`;

export const KnowledgeSuccessBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 8px;
  background: #d3f9d8;
  color: #2f9e44;
  font-size: 13px;
  font-weight: 500;
`;

// ─── Existing knowledge list (shown inside modal) ─────────────────────────────

export const KnowledgeExistingSection = styled.div`
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const KnowledgeExistingHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const KnowledgeExistingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 160px;
  overflow-y: auto;
  ${scrollbar}
`;

export const KnowledgeExistingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 7px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
`;

export const KnowledgeExistingInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const KnowledgeExistingName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1d2939;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const KnowledgeExistingMeta = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const KnowledgeExistingDeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #9ca3af;
  flex-shrink: 0;
  transition:
    background 0.12s,
    color 0.12s;

  &:hover {
    background: #fee2e2;
    color: #dc2626;
  }
`;

export const KnowledgeExistingEmpty = styled.div`
  font-size: 12.5px;
  color: #9ca3af;
  text-align: center;
  padding: 4px 0;
`;

export const InlineActionBtn = styled.button`
  display: block;
  width: 100%;
  padding: 6px 12px;
  margin: 2px 0;
  border: 1px solid #d0ebff;
  border-radius: 8px;
  background: #f0f8ff;
  color: #1971c2;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.13s,
    border-color 0.13s,
    box-shadow 0.13s;

  &:hover {
    background: #e7f5ff;
    border-color: #228be6;
    box-shadow: 0 1px 4px rgba(34, 139, 230, 0.15);
  }
`;

export const MarkdownTableWrapper = styled.div`
  overflow-x: auto;
  margin: 10px 0;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

export const MarkdownTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  line-height: 1.5;
`;

export const MarkdownThead = styled.thead`
  background: #f1f5f9;
`;

export const MarkdownTh = styled.th`
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
`;

export const MarkdownTr = styled.tr`
  &:nth-child(even) {
    background: #f8fafc;
  }
  &:hover {
    background: #eff6ff;
  }
`;

export const MarkdownTd = styled.td`
  padding: 7px 12px;
  color: #374151;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
`;
